import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import type { SnackbarVariant } from '../../components/Snackbar';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import ResearchNoteModal from './ResearchNoteModal';
import FloatingNewNoteButton from './FloatingNewNoteButton';
import ResearchGrowthFiscalTables from './ResearchGrowthFiscalTables';
import DataSourceAttribution from '../../components/DataSourceAttribution';

interface ResearchGrowthFiscalScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
  selectedPeriod: 'annual' | 'quarter';
  isAdmin?: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

const ResearchGrowthFiscalScreen: React.FC<ResearchGrowthFiscalScreenProps> = ({
  isDarkMode,
  selectedStock,
  selectedPeriod,
  isAdmin = false,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCategory, setNoteCategory] = useState('Growth Fiscal');
  const [noteContent, setNoteContent] = useState('');

  const [showFloatingButton, setShowFloatingButton] = useState<boolean>(true);

  useEffect(() => {
    const update = () => setShowFloatingButton(window.innerWidth >= 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const resolvedUserId =
    typeof authUserId === 'number' &&
    Number.isFinite(authUserId) &&
    authUserId > 0
      ? authUserId
      : undefined;
  const canPersistNotes = !!authToken && resolvedUserId != null;
  const canPersistScreenshots = !!authToken;

  const growthBookmarkTitle = useMemo(() => {
    return 'Growth Fiscal';
  }, []);

  const [savingGrowthBookmark, setSavingGrowthBookmark] = useState(false);
  const [didGrowthBookmark, setDidGrowthBookmark] = useState(false);
  const [growthBookmarkFlash, setGrowthBookmarkFlash] = useState(false);
  const growthBookmarkFlashTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (growthBookmarkFlashTimeoutRef.current) {
        try {
          window.clearTimeout(growthBookmarkFlashTimeoutRef.current);
        } catch {
          // ignore
        }
      }
    };
  }, []);

  useEffect(() => {
    setGrowthBookmarkFlash(false);
  }, [selectedStock]);

  const triggerGrowthBookmarkFlash = () => {
    setGrowthBookmarkFlash(true);
    if (growthBookmarkFlashTimeoutRef.current) {
      try {
        window.clearTimeout(growthBookmarkFlashTimeoutRef.current);
      } catch {
        // ignore
      }
    }
    growthBookmarkFlashTimeoutRef.current = window.setTimeout(() => {
      setGrowthBookmarkFlash(false);
    }, 30_000);
  };

  const notify = (message: string, variant?: SnackbarVariant) => {
    onNotify?.(message, variant);
    if (!onNotify && typeof window !== 'undefined') {
      if (variant === 'error') window.alert(message);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!canPersistScreenshots) {
        setDidGrowthBookmark(false);
        return;
      }

      setDidGrowthBookmark(false);

      try {
        const items = await adminApi.listResearchDeskScreenshots(authToken as string, {
          userId: resolvedUserId,
          sourcePage: 'Growth Fiscal',
          stockSymbol: selectedStock,
          title: growthBookmarkTitle,
          limit: 10,
        });

        if (cancelled) return;
        setDidGrowthBookmark(items.length > 0);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        if (cancelled) return;
        setDidGrowthBookmark(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [canPersistScreenshots, authToken, resolvedUserId, onUnauthorized, selectedStock, growthBookmarkTitle]);

  const toDataUrlWithFallback = (canvas: HTMLCanvasElement, type: string, quality: number) => {
    try {
      const dataUrl = canvas.toDataURL(type, quality);
      if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) return { type, dataUrl };
    } catch {
      // ignore and fall back
    }

    const fallbackType = 'image/png';
    try {
      return { type: fallbackType, dataUrl: canvas.toDataURL(fallbackType) };
    } catch {
      return { type: fallbackType, dataUrl: '' };
    }
  };

  const makeThumbDataUrl = async (canvas: HTMLCanvasElement, maxWidthPx: number, quality: number): Promise<string> => {
    const srcW = canvas.width;
    const srcH = canvas.height;
    if (!srcW || !srcH) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    const scale = Math.min(1, maxWidthPx / srcW);
    const dstW = Math.max(1, Math.floor(srcW * scale));
    const dstH = Math.max(1, Math.floor(srcH * scale));

    const thumb = document.createElement('canvas');
    thumb.width = dstW;
    thumb.height = dstH;
    const ctx = thumb.getContext('2d');
    if (!ctx) return toDataUrlWithFallback(canvas, 'image/webp', quality).dataUrl;

    ctx.drawImage(canvas, 0, 0, dstW, dstH);
    return toDataUrlWithFallback(thumb, 'image/webp', quality).dataUrl;
  };

  const captureGrowthPage = async (): Promise<HTMLCanvasElement | null> => {
    const el = document.getElementById('growth-fiscal-main-container');
    if (!el) return null;

    const bg = window.getComputedStyle(el).backgroundColor;
    const backgroundColor = !bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)' ? '#ffffff' : bg;

    const width = Math.max(1, (el as HTMLElement).scrollWidth || el.clientWidth || 1);
    const height = Math.max(1, (el as HTMLElement).scrollHeight || el.clientHeight || 1);
    const maxCanvasDim = 15000;
    const scaleCap = maxCanvasDim / Math.max(width, height);
    const scale = Math.max(0.5, Math.min(2, scaleCap || 1));

    try {
      return await html2canvas(
        el,
        {
          backgroundColor,
          scale,
          useCORS: true,
          logging: false,
        } as any
      );
    } catch {
      return null;
    }
  };

  const saveGrowthBookmark = async () => {
    if (!canPersistScreenshots) {
      notify('Please login to save screenshots.', 'error');
      return;
    }
    if (savingGrowthBookmark) return;
    if (growthBookmarkFlash) return;

    setSavingGrowthBookmark(true);
    try {
      const captured = await captureGrowthPage();
      if (!captured) {
        notify('Page is not ready to capture yet.', 'error');
        return;
      }

      const full = toDataUrlWithFallback(captured, 'image/webp', 0.92);
      const thumbDataUrl = await makeThumbDataUrl(captured, 1100, 0.82);

      if (!full.dataUrl || !thumbDataUrl) {
        notify('Failed encoding screenshot image.', 'error');
        return;
      }

      await adminApi.createResearchDeskScreenshot(authToken as string, {
        userId: resolvedUserId,
        sourcePage: 'Growth Fiscal',
        stockSymbol: selectedStock,
        title: growthBookmarkTitle,
        imageDataUrl: full.dataUrl,
        thumbDataUrl,
      });

      setDidGrowthBookmark(true);
      triggerGrowthBookmarkFlash();
      void `${selectedStock} - ${growthBookmarkTitle} - Screenshot saved`;
    } catch (e: any) {
      if (isUnauthorized(e)) {
        onUnauthorized?.();
        return;
      }
      notify(e?.message || 'Failed saving screenshot', 'error');
    } finally {
      setSavingGrowthBookmark(false);
    }
  };

  const hasNoteText =
    String(noteContent ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim().length > 0;

  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const canAddNote = noteCategory.trim().length > 0 && hasNoteText && noteTitle.trim().length > 0;

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  const openNewNote = () => {
    setNoteCategory('Growth Fiscal');
    setNoteTitle('');
    setNoteContent('');
    setIsNoteModalOpen(true);
  };

  return (
    <>
      <ResearchGrowthFiscalTables
        isDarkMode={isDarkMode}
        selectedStock={selectedStock}
        selectedPeriod={selectedPeriod}
        mode="page"
        showBookmarkButton
        isBookmarked={growthBookmarkFlash}
        isBookmarkBusy={savingGrowthBookmark}
        onBookmarkClick={saveGrowthBookmark}
        showSourceLabels={isAdmin}
      />

      <DataSourceAttribution isDarkMode={isDarkMode} style={{ marginLeft: '2rem' }} />

      <FloatingNewNoteButton
        visible={showFloatingButton}
        onClick={openNewNote}
        label="New Note"
        backgroundColor={isDarkMode ? '#374151' : undefined}
      />

      <ResearchNoteModal
        isOpen={isNoteModalOpen}
        onRequestClose={() => setIsNoteModalOpen(false)}
        isDarkMode={isDarkMode}
        noteTitle={noteTitle}
        onChangeTitle={setNoteTitle}
        noteCategory={noteCategory}
        onChangeCategory={setNoteCategory}
        noteContent={noteContent}
        onChangeContent={setNoteContent}
        categories={RESEARCH_DESK_NOTE_CATEGORIES}
        canSubmit={canAddNote}
        onSubmit={async () => {
          if (!canPersistNotes) {
            onNotify?.('Please login to save notes.', 'error');
            return;
          }

          const title = extractTitleFromContent(noteContent);
          if (!title) return;
          const associatedWith = noteCategory.trim();
          if (!associatedWith) return;

          try {
            await adminApi.createResearchDeskNote(authToken as string, {
              userId: resolvedUserId,
              title,
              associatedWith,
              stockSymbol: selectedStock,
              html: noteContent ?? '',
            });
            setIsNoteModalOpen(false);
            setNoteTitle('');
            setNoteContent('');
            setNoteCategory('Growth Fiscal');
            onNotify?.(`${selectedStock} - Growth Fiscal - Saved`, 'success');
          } catch (e: any) {
            if (isUnauthorized(e)) onUnauthorized?.();
          }
        }}
      />
    </>
  );
};

export default ResearchGrowthFiscalScreen;
